import API_BASE from "../../config/api";
import { auth } from "../../config/config-firebase";
import { isAdminMerchantMode } from "../../utils/adminMerchantMode";

export const ASPECT_RATIOS = ["16:9", "1:1", "4:3", "3:4", "9:16"];
export const DEFAULT_ASPECT_RATIO = "16:9";
export const REFERENCE_TYPES = ["image/png", "image/jpeg", "image/webp"];
export const REFERENCE_MAX_BYTES = 20 * 1024 * 1024;

const GENERATE_TIMEOUT_MS = 230000;
const DEFAULT_TIMEOUT_MS = 60000;

// The store currently selected in the app (kept in sync by MerchantHome / StoreSwitcher).
export const getActiveCustomerId = () => localStorage.getItem("customer_id") || "";

// Firebase ID token for merchants; admin JWT while impersonating; promo JWT for promo devices.
const getAuthToken = async () => {
  if (isAdminMerchantMode()) return localStorage.getItem("admin_token") || "";
  if (typeof auth.authStateReady === "function") await auth.authStateReady();
  if (auth.currentUser) return auth.currentUser.getIdToken();
  return localStorage.getItem("auth_token") || "";
};

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function request(path, { method = "GET", body, query, timeout = DEFAULT_TIMEOUT_MS } = {}) {
  const customerId = getActiveCustomerId();
  if (!customerId) throw new ApiError("No store selected. Please sign in again.", 400);

  const params = new URLSearchParams({ customerId, ...(query || {}) });
  const isForm = body instanceof FormData;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  let res;
  try {
    const token = await getAuthToken();
    res = await fetch(`${API_BASE}${path}?${params}`, {
      method,
      signal: controller.signal,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body && !isForm ? { "Content-Type": "application/json" } : {}),
      },
      body: body ? (isForm ? body : JSON.stringify({ ...body, customerId })) : undefined,
    });
  } catch (err) {
    if (err.name === "AbortError") throw new ApiError("This is taking longer than expected. Please try again.", 408);
    throw new ApiError("Network error. Check your connection and try again.", 0);
  } finally {
    clearTimeout(timer);
  }

  let data = {};
  try {
    data = await res.json();
  } catch {
    /* non-JSON body */
  }
  if (!res.ok || data.success === false) {
    const fallback =
      res.status === 401
        ? "Please sign in again."
        : res.status === 404
          ? "This feature isn't available on the server yet. Please restart or update the backend."
          : `Something went wrong (error ${res.status}). Please try again.`;
    throw new ApiError(data.message || fallback, res.status);
  }
  return data;
}

/**
 * Edit the reference image with OpenAI. The image is sent with every request and
 * is never stored on the server; the edited image comes back as a data URL.
 */
export const generateImage = ({ file, extractedFile, prompt, title, description, price, aspectRatio, promptId, matchImageToTitle, productImage, templateId, mode }) => {
  const form = new FormData();
  // `file` is omitted for "Prompt only" and when a saved Base Template supplies its own image.
  if (file) form.append("referenceImage", file);
  if (mode === "prompt") form.append("mode", "prompt");
  if (templateId) form.append("templateId", templateId);
  // Template mode: `file` is the template, this is the extracted product picture to place into it.
  if (extractedFile) form.append("extractedImage", extractedFile);
  form.append("prompt", prompt || "");
  form.append("title", title || "");
  form.append("description", description || "");
  form.append("price", price || "");
  form.append("aspectRatio", aspectRatio);
  if (promptId) form.append("promptId", promptId);
  // Set when the item came from Menu Data Extraction's "Change Image?" → Yes
  // choice: regenerate the depicted food/dish to match the title, not just swap text.
  if (matchImageToTitle) form.append("matchImageToTitle", "true");
  // "Product image" option: white-background product photo (fruits: whole + cut piece).
  if (productImage) form.append("productImage", "true");
  return request("/image-generation", { method: "POST", body: form, timeout: GENERATE_TIMEOUT_MS }).then((d) => d.data);
};

/**
 * Generate a brand-new, white-background image for one AI Menu item from just
 * its name -- no reference image. Used by the AI Menu card's "Generate Image"
 * button (see AIMenuScreen.js). Distinct from generateImage() above, which
 * edits an existing menu-screen image and requires a reference file.
 */
export const generateItemImage = ({ itemId, itemName }) =>
  request("/image-generation/item-image", {
    method: "POST",
    body: { itemId, itemName },
    timeout: GENERATE_TIMEOUT_MS,
  }).then((d) => d.data);

/**
 * Persists a generated (or otherwise supplied) image for one AI Menu item --
 * stored server-side in product_upc_images (item_id column), not in
 * menu_data. Called when the merchant clicks "Save" on the generated
 * preview.
 */
export const saveItemImage = ({ itemId, imageUrl }) =>
  request("/image-generation/item-image/save", {
    method: "POST",
    body: { itemId, imageUrl },
  }).then((d) => d.data);

/**
 * Upload a photo / video taken or chosen on the device for one AI Menu item. Stored the same
 * way as a saved generated image (S3 + product_upc_images); resolves to { itemId, url }.
 */
export const uploadItemMedia = ({ itemId, file }) => {
  const form = new FormData();
  form.append("itemId", itemId); // before the file, so the server sees it first
  form.append("media", file);
  return request("/image-generation/item-image/upload", {
    method: "POST",
    body: form,
    timeout: 180000,
  }).then((d) => d.data);
};

/**
 * "Enhance" on an AI Menu card: AI retouch of the item's saved image (same product, clean white
 * background, better light and sharpness). Nothing is replaced yet -- resolves to
 * { itemId, url, engine, fallback } with the enhanced image as a data URL for preview; it is stored only
 * when saveItemImage is called with it. fallback = the AI was unavailable and the standard enhance ran.
 */
export const enhanceItemImage = ({ itemId }) =>
  request("/image-generation/item-image/enhance", {
    method: "POST",
    body: { itemId },
    timeout: GENERATE_TIMEOUT_MS,
  }).then((d) => d.data);

/** "Remove image" on an AI Menu card -- deletes the saved row for this item. */
export const removeItemImage = ({ itemId }) =>
  request("/image-generation/item-image/remove", {
    method: "POST",
    body: { itemId },
  }).then((d) => d.data);

/**
 * Batch-fetch every saved image for a list of item ids in one call -- used
 * right after the menu loads so the AI Menu screen can show existing images
 * without embedding them in the menu_data payload. Returns { [itemId]: url }.
 */
export const listItemImages = (itemIds) => {
  const ids = (itemIds || []).filter(Boolean);
  if (ids.length === 0) return Promise.resolve({});
  // POST, not GET: a store's full item list can produce a query string long
  // enough to exceed the server's request-line/header size limit, which
  // fails at the network layer (net::ERR_FAILED) before any handler runs.
  // A JSON body has no such limit here (express.json({ limit: "50mb" })).
  return request("/image-generation/item-images", {
    method: "POST",
    body: { itemIds: ids.join(",") },
  }).then((d) => d.data);
};

export const getUsageSummary = () =>
  request("/image-generation/usage/summary", { query: { tzOffset: new Date().getTimezoneOffset() } }).then((d) => d.data);

// Saved prompts
export const listPrompts = () => request("/image-generation/prompts").then((d) => d.data || []);
// Prompts used to create Base Template backgrounds (kept apart from Image Creation's saved prompts).
export const listBackgroundPrompts = () => request("/image-generation/prompts", { query: { kind: "background" } }).then((d) => d.data || []);
export const saveBackgroundPrompt = (prompt) => request("/image-generation/prompts", { method: "POST", body: { kind: "background", prompt } }).then((d) => d.data);
export const createPrompt = (name, prompt) =>
  request("/image-generation/prompts", { method: "POST", body: { name, prompt } }).then((d) => d.data);
export const updatePrompt = (id, fields) =>
  request(`/image-generation/prompts/${id}`, { method: "PUT", body: fields }).then((d) => d.data);
export const deletePrompt = (id) => request(`/image-generation/prompts/${id}`, { method: "DELETE" });

// Homepage Workflow
export const listWorkflowComponents = () => request("/workflow/components").then((d) => d.data || []);
export const addWorkflowComponent = (type) =>
  request("/workflow/components", { method: "POST", body: { type } }).then((d) => d.data);
export const removeWorkflowComponent = (id) => request(`/workflow/components/${id}`, { method: "DELETE" });

// Base Templates (Workflow → Base Template Creation); saved per store, applied by Image Creation via templateId.
export const listTemplates = () => request("/base-templates").then((d) => d.data || []);
export const createTemplate = (fields) => request("/base-templates", { method: "POST", body: fields }).then((d) => d.data);
export const updateTemplate = (id, fields) => request(`/base-templates/${id}`, { method: "PUT", body: fields }).then((d) => d.data);
export const approveTemplate = (id) => request(`/base-templates/${id}/approve`, { method: "POST", body: {} }).then((d) => d.data);
export const deleteTemplate = (id) => request(`/base-templates/${id}`, { method: "DELETE" });
// Creates the store's starting template from its onboarding details, once (safe to call repeatedly).
export const ensureInitialTemplate = () => request("/base-templates/ensure-initial", { method: "POST", body: {} }).then((d) => d.data);
// Reference image / logo for a template. Resolves to { key, url }.
export const uploadTemplateAsset = (file, kind) => {
  const form = new FormData();
  form.append("kind", kind === "logo" ? "logo" : "reference");
  form.append("asset", file);
  return request("/base-templates/assets", { method: "POST", body: form, timeout: 120000 }).then((d) => d.data);
};
// "Create a background with AI" from a text prompt. Slow (an image model call), so a long timeout. Resolves to { key, url };
// nothing is attached to a template until the caller saves it.
export const generateTemplateBackground = ({ prompt, aspectRatio, colors }) =>
  request("/base-templates/background", { method: "POST", body: { prompt, aspectRatio, colors }, timeout: 240000 }).then((d) => d.data);
// Server-side check of product records parsed from a JSON / Excel file (the server never receives the file itself).
export const validateProductRecords = (records) =>
  request("/product-records/validate", { method: "POST", body: { records } }).then((d) => d.data);

// Video Download (Workflow → Video Download). The server downloads the video (YouTube / Instagram / direct link) to S3;
// publishing then adds it to one of the store's Modes. Resolves to { downloadId, url, size, contentType, source }.
export const downloadVideoFromUrl = ({ url, confirmOwnership }) =>
  request("/video-download", { method: "POST", body: { url, confirmOwnership: confirmOwnership === true }, timeout: 250000 }).then((d) => d.data);
export const publishVideoToMode = ({ downloadId, modeId, title }) =>
  request("/video-download/publish", { method: "POST", body: { downloadId, modeId, title } }).then((d) => d.data);

/** Save a generated image (a data URL) to the visitor's device. */
export const downloadDataUrl = (dataUrl, filename) => {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const slugify = (s) =>
  String(s || "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

export const formatNumber = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());

/**
 * Image Extraction: send rendered PDF pages + the user's request; the server finds
 * the named product, crops it and enhances it on white. Nothing is stored.
 * Resolves to [{ page, label, confidence, originalDataUrl, enhancedDataUrl, error }].
 */
export const extractProductImages = ({ pages, prompt, single = false }) => {
  const form = new FormData();
  pages.forEach((blob, i) => form.append("pages", blob, `page-${i + 1}.jpg`));
  form.append("prompt", prompt || "");
  if (single) form.append("single", "1"); // the request names exactly one product
  return request("/image-extraction", { method: "POST", body: form, timeout: GENERATE_TIMEOUT_MS }).then((d) => ({
    results: d.results || [],
    items: d.items || [], // the products the AI found in the request
    message: d.message || "",
  }));
};

/**
 * Image Extraction (fast path): thumbnails of the PDF's embedded images + the text
 * printed next to each → which of them show the requested product.
 * Resolves to { matches: [{ id, item, label, confidence }], items: string[] } — `item` is the product the
 * customer asked for that the picture belongs to; `items` are all the products named in the request.
 */
export const locateImageCandidates = ({ thumbs, meta, prompt, single = false }) => {
  const form = new FormData();
  thumbs.forEach((blob, i) => form.append("thumbs", blob, `thumb-${i + 1}.jpg`));
  form.append("meta", JSON.stringify(meta));
  form.append("prompt", prompt || "");
  if (single) form.append("single", "1");
  return request("/image-extraction/locate", { method: "POST", body: form, timeout: 60000 }).then((d) => ({
    matches: d.matches || [],
    items: d.items || [],
  }));
};

/**
 * Check ONE chosen picture before it is extracted: is it really the product that was named, and the only
 * product shown? Resolves to { status: "verified"|"unsure"|"rejected"|"skipped", confidence, shows,
 * readableText, reason }. Rejects (throws) if the check itself fails -- callers keep the picture, unchecked.
 */
export const verifyExtractedImage = ({ blob, name, text = "" }) => {
  const form = new FormData();
  form.append("name", name);
  form.append("text", text);
  form.append("image", blob, "image.jpg");
  return request("/image-extraction/verify", { method: "POST", body: form, timeout: 90000 });
};

/**
 * Enhance ONE picture (the exact image from the PDF) with AI: same product on a clean white background,
 * better light and sharpness. Nothing is stored. If the AI is unavailable the server uses the standard
 * enhance instead and says so (`fallback: true`). AI enhancement is slower, hence the long timeout
 * (the server queues several pictures).
 * Resolves to { enhancedDataUrl, width, height, backgroundReplaced, upscaled, engine, fallback }.
 */
export const enhanceExtractedImage = (blob) => {
  const form = new FormData();
  form.append("image", blob, "image.png");
  return request("/image-extraction/enhance", { method: "POST", body: form, timeout: GENERATE_TIMEOUT_MS });
};
